/**
 * @mentions in Flag discussions (Traci's review, 23 Sep 2026; v109 §7). Pure.
 *
 *   Type @ and the first letters of a name; pick from active staff. A mention notifies that person (a System item,
 *   "mentioned in a flag") and lets them open, read and reply to that one flag. It does not give them the owning
 *   department's other flags, and they cannot resolve it: Admin or the owning department resolves.
 */

/** The partial name being typed after the last "@" before the caret, or null. "@Ke" -> "Ke". */
export function mentionQuery(text, caret = String(text || '').length) {
  const before = String(text || '').slice(0, caret)
  const m = before.match(/(^|\s)@([A-Za-z][A-Za-z'-]*)?$/)
  return m ? (m[2] || '') : null
}

/** Staff whose first or full name starts with the query (case-insensitive), at most `limit`. */
export function matchStaff(staff, query, limit = 8) {
  const q = String(query || '').toLowerCase()
  return (staff || []).filter(s => {
    const name = String(s.name || '').toLowerCase()
    return !q || name.startsWith(q) || name.split(/\s+/).some(part => part.startsWith(q))
  }).slice(0, limit)
}

/** Replace the "@partial" before the caret with "@Full Name ". Returns { text, caret }. */
export function insertMention(text, caret, person) {
  const s = String(text || '')
  const before = s.slice(0, caret)
  const after = s.slice(caret)
  const start = before.lastIndexOf('@')
  const head = before.slice(0, start)
  const token = `@${person.name} `
  return { text: head + token + after, caret: (head + token).length }
}

/** Staff ids mentioned in a body: "@Full Name" first, then "@FirstName" when that first name is unique. */
export function parseMentions(body, staff) {
  const text = String(body || '')
  const found = new Set()
  const people = (staff || []).filter(s => s.name).sort((a, b) => b.name.length - a.name.length)
  const lower = text.toLowerCase()
  for (const p of people) {
    if (lower.includes('@' + p.name.toLowerCase())) found.add(p.id)
  }
  const firsts = new Map()
  for (const p of people) {
    const f = p.name.split(/\s+/)[0].toLowerCase()
    firsts.set(f, firsts.has(f) ? null : p.id)
  }
  for (const m of text.matchAll(/(^|\s)@([A-Za-z][A-Za-z'-]*)/g)) {
    const id = firsts.get(m[2].toLowerCase())
    if (id) found.add(id)
  }
  return [...found]
}

const lower = v => String(v || '').toLowerCase()

/** May this person read and reply to the flag? Admin, the owning department, or anyone mentioned on it. */
export function canComment(flag, { roles = [], userId = null, mentionedIds = [] } = {}) {
  const held = new Set([...roles].map(lower))
  if (held.has('admin')) return true
  if (flag?.assigned_to_role && held.has(lower(flag.assigned_to_role))) return true
  if (userId && flag?.assigned_to === userId) return true
  return Boolean(userId && mentionedIds.includes(userId))
}

/** May this person resolve it? Admin or anyone in the owning department (never only because they were mentioned). */
export function canResolveFlag(flag, roles = []) {
  if (!flag || lower(flag.status) === 'resolved') return false
  const held = new Set([...roles].map(lower))
  return held.has('admin') || Boolean(flag.assigned_to_role && held.has(lower(flag.assigned_to_role)))
}

/** Flag sub-filters (prototype v108 §8.2): Unread · Active · Resolved · All. Active = anything not resolved. */
export function flagFilter(items, status) {
  if (status === 'unread') return items.filter(i => !i.read)
  if (status === 'active') return items.filter(i => i.flagStatus !== 'resolved')
  if (status === 'resolved') return items.filter(i => i.flagStatus === 'resolved')
  return items
}
